import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Loader2, Eye, EyeOff } from "lucide-react";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { Seo } from "@/components/Seo";

const field = "w-full bg-[color:var(--charcoal)] border border-[color:var(--line)] rounded-xl px-4 py-3.5 text-white placeholder:text-[color:var(--chrome-3)] focus:outline-none focus:border-[color:var(--cyan)] transition-colors";

export default function SignIn() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email.trim().toLowerCase(), password);
      navigate("/vault", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-black min-h-screen flex items-center justify-center px-5 py-20">
      <Seo title="Sign In — CardScience HQ" description="Sign in to your CardScience HQ vault." path="/signin" noindex />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
        className="w-full max-w-md rounded-3xl holo-border holo-glow p-8 bg-black/60 backdrop-blur-xl">
        <p className="font-tech text-[11px] tracking-[0.4em] text-[color:var(--chrome-3)] text-center">CARDSCIENCE<span className="text-holo">HQ</span></p>
        <h1 className="h-display text-3xl text-white text-center mt-4">WELCOME BACK</h1>
        <p className="text-sm text-[color:var(--chrome-3)] text-center mt-2">Enter your vault.</p>

        <form onSubmit={submit} className="mt-7 space-y-3">
          <input data-testid="signin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className={field} />
          <div className="relative">
            <input data-testid="signin-password" type={show ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className={field} />
            <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[color:var(--chrome-3)] hover:text-white">
              {show ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>

          {error && <p data-testid="signin-error" className="text-sm text-[color:var(--magenta)]">{error}</p>}

          <button type="submit" data-testid="signin-submit" disabled={busy}
            className="btn-holo w-full rounded-xl py-4 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50">
            {busy ? <><Loader2 size={16} className="animate-spin" /> SIGNING IN…</> : <>SIGN IN <ArrowRight size={16} /></>}
          </button>
        </form>

        <p className="text-sm text-[color:var(--chrome-3)] text-center mt-5">
          New here? <Link to="/join" className="text-[color:var(--cyan)] hover:underline">Open your free account</Link>
        </p>
      </motion.div>
    </div>
  );
}
